import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { BookOpen, Users, Trophy, ArrowRight } from 'lucide-react'
import { Card, CardContent } from '@/components/ui'
import { WaitlistSignup } from '@/components/WaitlistSignup'
import { LogoSvg } from '@/assets/brand/logos'
import { useAuth } from '@/context/AuthContext'

export function Landing() {
  const { isConfigured } = useAuth()

  const features = [
    {
      icon: BookOpen,
      title: 'Story Steps',
      description: 'Learn how great stories work with a simple 5-step path: Beginning, Problem, Adventure, Climax, and Resolution.',
      color: 'var(--color-kids-primary)',
    },
    {
      icon: Users,
      title: 'Character Cards',
      description: 'Create characters with wants, fears, and personality traits that make them feel real.',
      color: 'var(--color-kids-secondary)',
    },
    {
      icon: Trophy,
      title: 'Earn Badges',
      description: 'Complete writing missions and earn badges as you grow into a storyteller.',
      color: 'var(--color-kids-accent)',
    },
  ]

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)]">
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[var(--color-kids-border)]">
        <div className="container mx-auto pl-7 pr-6 h-[76px] flex items-center justify-between">
          {/* Logo - brand anchor with subtle hover */}
          <Link to="/" className="flex items-center transition-transform duration-200 ease-out hover:scale-[1.02] hover:opacity-90">
            <img src={LogoSvg} alt="Inkwell Jr." className="h-[44px] md:h-[52px]" />
          </Link>

          {/* Coming Soon Pill - secondary status */}
          <span className="px-3 py-1.5 rounded-full bg-slate-50 text-slate-400 font-medium text-xs ring-1 ring-slate-200">
            Coming Soon
          </span>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 -z-10">
          <div
            className="absolute top-20 left-10 w-64 h-64 rounded-full opacity-20 blur-3xl"
            style={{ background: 'var(--color-kids-primary)' }}
          />
          <div
            className="absolute top-40 right-20 w-48 h-48 rounded-full opacity-20 blur-3xl"
            style={{ background: 'var(--color-kids-secondary)' }}
          />
          <div
            className="absolute bottom-20 left-1/3 w-32 h-32 rounded-full opacity-20 blur-3xl"
            style={{ background: 'var(--color-kids-accent)' }}
          />
        </div>

        <div className="container mx-auto px-6 py-16 md:py-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            {/* Narrative hook - secondary, lead-in */}
            <p className="text-sm font-medium text-slate-500 mb-2">
              Every great writer starts somewhere
            </p>

            {/* Headline */}
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-[var(--color-kids-text)] mb-6 leading-tight">
              Where Young Writers
              <br />
              <span className="text-[var(--color-kids-primary)]">Become Storytellers</span>
            </h1>

            {/* Tightened copy - concrete, meaning, promise */}
            <p className="text-lg md:text-xl text-[var(--color-kids-text-muted)] max-w-2xl mx-auto mb-10 leading-relaxed">
              Every great writer starts with a story they care about.
              In Inkwell Jr., writing isn't just practice — it's a journey.
              Each mission helps your child explore ideas, make choices, and learn how stories come to life.
            </p>

            {/* CTA */}
            <WaitlistSignup variant="hero" />

            {/* Explore link with arrow */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-10"
            >
              <Link
                to="/how-it-works"
                className="group inline-flex items-center gap-2 px-6 py-3 rounded-[var(--radius-kid)] bg-[var(--color-kids-secondary)] text-white font-semibold shadow-[var(--shadow-kid)] hover:bg-[var(--color-kids-secondary-dark)] hover:shadow-[var(--shadow-kid-hover)] transition-all"
              >
                See how it works
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-6 py-16 md:py-20">
        {/* Narrative bridge */}
        <p className="text-center text-[var(--color-kids-text-muted)] mb-3 max-w-lg mx-auto">
          Great stories follow patterns writers have used for generations.
        </p>
        <h2 className="font-display text-sm md:text-base font-semibold tracking-wide text-center text-[var(--color-kids-text-muted)] uppercase mb-12">
          Everything You Need to Write Great Stories
        </h2>
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card hoverable className="h-full">
                <CardContent className="p-6">
                  {/* Icon and title in a row */}
                  <div className="flex items-center gap-4 mb-4">
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center"
                      style={{
                        background: `color-mix(in srgb, ${feature.color} 12%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${feature.color} 20%, transparent)`,
                      }}
                    >
                      <feature.icon
                        className="w-7 h-7"
                        style={{ color: feature.color }}
                      />
                    </div>
                    <h3 className="font-display text-lg font-bold text-[var(--color-kids-text)]">
                      {feature.title}
                    </h3>
                  </div>
                  <p className="text-[var(--color-kids-text-muted)] leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section - new framing */}
      <section className="bg-[var(--color-kids-primary)] py-16 md:py-20">
        <div className="container mx-auto px-6 text-center">
          <p className="text-white/60 text-xs font-medium mb-3 max-w-md mx-auto">
            Give your child a place to begin.
          </p>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-4">
            Be First to Try Inkwell Jr.
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
            Join the first wave of young storytellers. We'll email you when early access opens.
          </p>
          <WaitlistSignup variant="cta" />
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-6 py-10 text-center">
        <p className="text-sm text-[var(--color-kids-text-muted)]">
          Made with love for young storytellers everywhere.
        </p>
        {!isConfigured && (
          <p className="mt-2 text-sm text-[var(--color-kids-warning)]">
            Note: Supabase is not configured. Copy .env.example to .env.local and add your credentials.
          </p>
        )}
      </footer>
    </div>
  )
}
